import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import * as L from 'leaflet';
import { Subscription } from 'rxjs';
import { PwaService } from '../../services/pwa.service';
import { LocationService } from '../../services/location.service';

export interface MapPointer {
  lat: number;
  lng: number;
  title?: string;
  subtitle?: string;
}

@Component({
  selector: 'app-map-view',
  standalone: false,
  templateUrl: './map-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./map-view.component.scss']
})
export class MapViewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() centerMap: { lat: number; lng: number } = { lat: 51.9355, lng: 15.5064 };
  @Input() zoomMap = 18;
  @Input() pointerLocal: MapPointer | null = null;
  /** When true the map renders without a required pointer and emits click coordinates */
  @Input() interactive = false;

  @Output() locationPicked = new EventEmitter<{ lat: number; lng: number }>();

  @ViewChild('mapContainer') mapContainer?: ElementRef<HTMLDivElement>;

  mapView = true;
  satelliteView = false;
  currentLocation?: { lat: number; lng: number };
  autoLocationUpdateEnabled = false;
  isGeolocationSupported = false;

  private leafletMap: L.Map | null = null;
  private resizeObserver?: ResizeObserver;
  private pointerMarker: L.Marker | null = null;
  private currentLocationMarker: L.Marker | null = null;
  private routeLine: L.Polyline | null = null;
  private routeMarkers: L.Marker[] = [];
  private locationSubscription?: Subscription;
  private lastCenterKey: string | null = null;
  private lastPointerKey: string | null = null;
  private _pendingShowPopup = false;
  public isMobile = false;
  
  constructor(
    private pwaService: PwaService,
    private locationService: LocationService
  ) {
    this.isGeolocationSupported = this.locationService.isGeolocationSupported();
    
    // Subscribe to location updates
    this.locationSubscription = this.locationService.currentLocation$.subscribe(location => {
      if (location) {
        this.currentLocation = location;
      }
    });
  }

  ngAfterViewInit(): void {
    this.isMobile = this.pwaService.isMobileRunning;
    if (!this.mapContainer) {
      return;
    }

    this.leafletMap = L.map(this.mapContainer.nativeElement).setView(
      [this.centerMap.lat, this.centerMap.lng],
      this.zoomMap
    );

    this.switchToNormalView();
    this.syncCenter(true);
    this.updatePointerMarkerInternal(true);

    if (this.interactive) {
      this.leafletMap.on('click', (e: L.LeafletMouseEvent) => {
        this._pendingShowPopup = true;
        this.locationPicked.emit({ lat: e.latlng.lat, lng: e.latlng.lng });
      });
    }

    // Use ResizeObserver so Leaflet recalculates tile coverage whenever the
    // CSS grid/flex container reaches its final dimensions (grey-tile fix).
    // Falls back to a generous timeout when ResizeObserver is unavailable.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.leafletMap?.invalidateSize();
      });
      this.resizeObserver.observe(this.mapContainer.nativeElement);
    } else {
      setTimeout(() => this.leafletMap?.invalidateSize(), 300);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.leafletMap) {
      return;
    }

    if (changes['centerMap'] || changes['zoomMap']) {
      this.syncCenter();
      // Re-check size after repositioning (async data may have settled layout)
      this.leafletMap.invalidateSize();
    }

    if (changes['pointerLocal']) {
      this.updatePointerMarker();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.locationService.stopAutoLocationUpdate();
    this.locationSubscription?.unsubscribe();
    this.clearRoute();
    this.pointerMarker = null;
    this.currentLocationMarker = null;
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
  }

  hasValidPointer(): boolean {
    if (!this.pointerLocal) {
      return false;
    }
    return Number.isFinite(this.pointerLocal.lat) && Number.isFinite(this.pointerLocal.lng);
  }

  switchToNormalView() {
    this.satelliteView = false;
    if (!this.leafletMap) {
      return;
    }

    this.leafletMap.eachLayer(layer => {
      if (layer instanceof L.TileLayer) {
        this.leafletMap!.removeLayer(layer);
      }
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.leafletMap);
  }

  switchToSatelliteView() {
    this.satelliteView = true;
    if (!this.leafletMap) {
      return;
    }

    this.leafletMap.eachLayer(layer => {
      if (layer instanceof L.TileLayer) {
        this.leafletMap!.removeLayer(layer);
      }
    });

    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
    }).addTo(this.leafletMap);
  }

  showCurrentLocation(enableAutoUpdate: boolean = false) {
    if (!this.leafletMap) return;

    if (!this.isGeolocationSupported) {
      alert('Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.');
      return;
    }

    this.locationService.getCurrentLocationWithErrorHandling().subscribe({
      next: (location) => {
        this.currentLocation = location;
        this.leafletMap!.setView([location.lat, location.lng], this.zoomMap);
        this.updateCurrentLocationMarker(location.lat, location.lng, 'Twoja lokalizacja');
        
        if (enableAutoUpdate) {
          this.locationService.startAutoLocationUpdate();
          this.autoLocationUpdateEnabled = true;
        }
      },
      error: (error) => {
        alert(error.message);
      }
    });
  }

  toggleAutoLocationUpdate() {
    if (!this.isGeolocationSupported) {
      alert('Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.');
      return;
    }

    this.locationService.toggleAutoLocationUpdate().subscribe({
      next: (enabled) => {
        this.autoLocationUpdateEnabled = enabled;
      },
      error: (error) => {
        let errorMessage = 'Nie można włączyć automatycznych aktualizacji lokalizacji. ';
        switch (error.type) {
          case 'PERMISSION_DENIED':
            errorMessage += 'Dostęp do lokalizacji został zablokowany. Sprawdź ustawienia przeglądarki.';
            break;
          case 'POSITION_UNAVAILABLE':
            errorMessage += 'Informacje o lokalizacji są niedostępne.';
            break;
          case 'TIMEOUT':
            errorMessage += 'Przekroczono czas oczekiwania na pobranie lokalizacji. Spróbuj ponownie.';
            break;
          default:
            errorMessage += 'Wystąpił nieznany błąd podczas pobierania lokalizacji.';
            break;
        }
        alert(errorMessage);
      }
    });
  }

  getAutoLocationUpdateStatus(): string {
    return this.locationService.getAutoLocationUpdateStatus();
  }

  navigateToPointer() {
    if (!this.leafletMap || !this.pointerLocal || !this.hasValidPointer()) {
      alert('Brak poprawnej lokalizacji celu do nawigacji.');
      return;
    }

    if (this.currentLocation) {
      this.drawRoute(this.currentLocation, { lat: this.pointerLocal.lat, lng: this.pointerLocal.lng });
      return;
    }

    this.locationService.getCurrentLocation().subscribe({
      next: (location) => {
        this.currentLocation = location;
        this.updateCurrentLocationMarker(location.lat, location.lng, 'Twoja lokalizacja');
        this.drawRoute(location, { lat: this.pointerLocal!.lat, lng: this.pointerLocal!.lng });
      },
      error: () => {
        alert('Nie można pobrać Twojej lokalizacji do wyznaczenia trasy.');
      }
    });
  }

  clearRoute() {
    if (!this.leafletMap) {
      return;
    }

    if (this.routeLine) {
      this.leafletMap.removeLayer(this.routeLine);
      this.routeLine = null;
    }

    this.routeMarkers.forEach(marker => this.leafletMap!.removeLayer(marker));
    this.routeMarkers = [];
  }



  private updatePointerMarker() {
    this.updatePointerMarkerInternal(false);
  }

  private updatePointerMarkerInternal(force: boolean) {
    if (!this.leafletMap || !this.pointerLocal || !this.hasValidPointer()) {
      if (this.pointerMarker && this.leafletMap) {
        this.leafletMap.removeLayer(this.pointerMarker);
        this.pointerMarker = null;
      }
      this.lastPointerKey = null;
      return;
    }

    const currentPointerKey = `${this.pointerLocal.lat}|${this.pointerLocal.lng}|${this.pointerLocal.title || ''}|${this.pointerLocal.subtitle || ''}`;
    if (!force && this.lastPointerKey === currentPointerKey) {
      return;
    }

    if (this.pointerMarker) {
      this.leafletMap.removeLayer(this.pointerMarker);
      this.pointerMarker = null;
    }

    const greenIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -41],
      shadowSize: [41, 41]
    });

    const title = this.pointerLocal.title || 'Lokalizacja';
    const subtitle = this.pointerLocal.subtitle || 'Punkt docelowy';

    this.pointerMarker = L.marker([this.pointerLocal.lat, this.pointerLocal.lng], { icon: greenIcon })
      .addTo(this.leafletMap)
      .bindPopup(`<strong>${title}</strong><br/><em>${subtitle}</em>`, { minWidth: 180 });

    // In interactive mode only open the popup when the user clicked the map.
    // In read-only mode always open it (delayed to let invalidateSize settle).
    if (!this.interactive) {
      setTimeout(() => this.pointerMarker?.openPopup(), 350);
    } else if (this._pendingShowPopup) {
      this._pendingShowPopup = false;
      setTimeout(() => this.pointerMarker?.openPopup(), 50);
    }

    this.lastPointerKey = currentPointerKey;
  }

  private syncCenter(force: boolean = false) {
    if (!this.leafletMap) {
      return;
    }

    const currentCenterKey = `${this.centerMap.lat}|${this.centerMap.lng}|${this.zoomMap}`;
    if (!force && this.lastCenterKey === currentCenterKey) {
      return;
    }

    this.leafletMap.setView([this.centerMap.lat, this.centerMap.lng], this.zoomMap);
    this.lastCenterKey = currentCenterKey;
  }

  private updateCurrentLocationMarker(lat: number, lng: number, popupText: string) {
    if (!this.leafletMap) return;

    if (this.currentLocationMarker) {
      this.leafletMap.removeLayer(this.currentLocationMarker);
      this.currentLocationMarker = null;
    }

    const redIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    this.currentLocationMarker = L.marker([lat, lng], { icon: redIcon })
      .addTo(this.leafletMap)
      .bindPopup(popupText);
  }

  private drawRoute(from: { lat: number; lng: number }, to: { lat: number; lng: number }) {
    if (!this.leafletMap) {
      return;
    }

    this.clearRoute();

    const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson&steps=true`;

    fetch(osrmUrl)
      .then(response => response.json())
      .then(data => {
        if (!data.routes || data.routes.length === 0) {
          alert('Nie znaleziono trasy do wskazanego punktu.');
          return;
        }

        const route = data.routes[0];
        const coordinates = route.geometry.coordinates;
        const latLngs = coordinates.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);

        this.routeLine = L.polyline(latLngs, {
          color: '#2196F3',
          weight: 5,
          opacity: 0.8,
          dashArray: '10, 5'
        }).addTo(this.leafletMap!);

        const startIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const endIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const startMarker = L.marker([from.lat, from.lng], { icon: startIcon })
          .addTo(this.leafletMap!)
          .bindPopup('Start - Twoja lokalizacja');

        const endMarker = L.marker([to.lat, to.lng], { icon: endIcon })
          .addTo(this.leafletMap!)
          .bindPopup('Cel - Punkt docelowy');

        this.routeMarkers = [startMarker, endMarker];
        this.leafletMap!.fitBounds(this.routeLine.getBounds().pad(0.1));
      })
      .catch(() => {
        alert('Błąd podczas wyznaczania trasy. Spróbuj ponownie.');
      });
  }

}
